import type { ProjectDefinition } from "../../../types";
import {
  type CellValue,
  type FuncionProjectSpec,
  type LabelledCell,
  columnLetter,
  conditions,
  excelDate,
  parseAddress,
} from "../spec";

const SHEET = "MD Calendario";

const IN = {
  lunes: excelDate("2026-01-05"),
  folio: 1001,
  pagos: 12,
  credito: 120000,
  primerPago: excelDate("2026-02-01"),
  mes: excelDate("2026-03-01"),
};

const firstOfMonth = (year: number, monthIndex: number) =>
  (Date.UTC(year, monthIndex, 1) - Date.UTC(1899, 11, 30)) / 86400000;
// DIASEM(fecha,2): lunes = 1 … domingo = 7.
const weekdayMon1 = (serial: number) => ((Math.floor(serial) + 5) % 7) + 1;
const lunesCalendario = IN.mes - weekdayMon1(IN.mes) + 1;

const EXPECTED: CellValue[][][] = [
  Array.from({ length: 12 }, (_, i) => [i + 1]),
  [Array.from({ length: 12 }, (_, i) => i + 1)],
  Array.from({ length: 8 }, (_, i) => [IN.lunes + i * 7]),
  Array.from({ length: 10 }, (_, i) => [`FAC-${IN.folio + i}`]),
  Array.from({ length: IN.pagos }, (_, i) => [firstOfMonth(2026, 1 + i)]),
  Array.from({ length: IN.pagos }, (_, i) => [IN.credito - ((i + 1) * IN.credito) / IN.pagos]),
  Array.from({ length: 6 }, (_, r) => Array.from({ length: 7 }, (_, c) => lunesCalendario + r * 7 + c)),
];

const LABELS = [
  "Tarea 1 · Números del 1 al 12 (vertical)",
  "Tarea 2 · Números del 1 al 12 (horizontal)",
  "Tarea 3 · Lunes del bimestre",
  "Tarea 4 · Folios de factura",
  "Tarea 5 · Fechas de pago",
  "Tarea 6 · Saldo después de cada pago",
  "Tarea 7 · Calendario de marzo",
];
const FORMATS: (string | undefined)[] = [undefined, undefined, "dd/mm/yyyy", undefined, "dd/mm/yyyy", "$#,##0", "d"];
const FNS = [
  ["SEQUENCE"], ["SEQUENCE"], ["SEQUENCE"], ["SEQUENCE"], ["SEQUENCE", "DATE"], ["SEQUENCE"],
  ["SEQUENCE", "WEEKDAY"],
];

const ANCHORS: string[] = [];
EXPECTED.reduce((row, result) => {
  ANCHORS.push(`D${row}`);
  return row + result.length + 3;
}, 12);

const spillRange = (anchor: string, result: CellValue[][]) => {
  const { col, row } = parseAddress(anchor);
  return `${anchor}:${columnLetter(col + result[0].length - 1)}${row + result.length - 1}`;
};

const answers: LabelledCell[] = ANCHORS.map((address, i) => ({
  address,
  label: LABELS[i],
  labelPosition: "above",
  areas: FORMATS[i] ? [{ range: spillRange(address, EXPECTED[i]), format: FORMATS[i]! }] : undefined,
}));

export const spec: FuncionProjectSpec = {
  projectId: "funciones-secuencia",
  sheets: [
    {
      name: SHEET,
      title: "Calendarios, folios y planes de pago",
      subtitle: "Listas que se generan solas — escribe una fórmula por celda amarilla",
      tables: [],
      inputs: [
        { address: "B4", label: "Primer lunes", value: IN.lunes, format: "dd/mm/yyyy" },
        { address: "B5", label: "Folio inicial", value: IN.folio },
        { address: "B6", label: "Número de pagos", value: IN.pagos },
        { address: "B7", label: "Monto del crédito", value: IN.credito, format: "$#,##0" },
        { address: "B8", label: "Primer pago", value: IN.primerPago, format: "dd/mm/yyyy" },
        { address: "B9", label: "Mes del calendario", value: IN.mes, format: "mmmm yyyy" },
      ],
      answers,
    },
  ],
  checks: Object.fromEntries(
    EXPECTED.map((expected, i) => [
      i + 1,
      { kind: "spill" as const, sheet: SHEET, address: ANCHORS[i], fns: FNS[i], expected },
    ])
  ),
};

const F = (text: string) => `<span class="formula">${text}</span>`;

export const project: ProjectDefinition = {
  id: "funciones-secuencia",
  title: "SECUENCIA: Calendarios, folios y planes de pago",
  subtitle: "Generar listas numéricas sin arrastrar",
  description:
    "En lugar de escribir 1, 2, 3… o arrastrar fechas, SECUENCIA genera listas completas con una fórmula: números, fechas cada 7 días, folios de factura, el calendario de pagos de un crédito o un calendario mensual.\n\nSECUENCIA(filas, columnas, inicio, incremento). Solo el primer argumento es obligatorio.",
  topics: [
    "Filas, columnas, inicio e incremento",
    "Secuencias de fechas",
    "Unir texto y números con &",
    "Fechas mensuales con FECHA",
    "Un calendario de 6 × 7",
  ],
  tasks: [
    {
      id: 1,
      level: "basico",
      title: "Tarea 1: Del 1 al 12 hacia abajo",
      description: `En la hoja <strong>MD Calendario</strong>, celda <strong>${ANCHORS[0]}</strong>: genera los números del <strong>1 al 12</strong> en una columna.`,
      solution: [`Selecciona ${F(ANCHORS[0])}.`, `Escribe ${F("=SECUENCIA(12)")}.`],
      expectedResult: "1, 2, 3 … 12 hacia abajo",
      finalFormula: "=SECUENCIA(12)",
    },
    {
      id: 2,
      level: "basico",
      title: "Tarea 2: Del 1 al 12 hacia la derecha",
      description: `En <strong>${ANCHORS[1]}</strong>: genera los números del <strong>1 al 12</strong> en <strong>una fila</strong> (hacia la derecha).`,
      solution: [
        `Selecciona ${F(ANCHORS[1])}.`,
        "El primer argumento son las filas y el segundo las columnas: 1 fila y 12 columnas.",
        `Escribe ${F("=SECUENCIA(1,12)")}.`,
      ],
      expectedResult: "1 a 12 en una sola fila",
      finalFormula: "=SECUENCIA(1,12)",
    },
    {
      id: 3,
      level: "intermedio",
      title: "Tarea 3: Los lunes de las próximas 8 semanas",
      description: `En <strong>${ANCHORS[2]}</strong>: genera <strong>8 fechas</strong>, empezando en el lunes de <strong>B4</strong> y avanzando <strong>de 7 en 7 días</strong>.`,
      solution: [
        `Selecciona ${F(ANCHORS[2])}.`,
        "Las fechas son números, así que SECUENCIA puede empezar en una fecha (inicio) y sumar 7 en cada paso (incremento).",
        `Escribe ${F("=SECUENCIA(8,1,B4,7)")}. El área ya tiene formato de fecha.`,
      ],
      expectedResult: "05/01/2026, 12/01/2026 … 8 lunes",
      finalFormula: "=SECUENCIA(8,1,B4,7)",
    },
    {
      id: 4,
      level: "intermedio",
      title: "Tarea 4: Folios de factura",
      description: `En <strong>${ANCHORS[3]}</strong>: genera <strong>10 folios</strong> con el formato <strong>FAC-1001</strong>, <strong>FAC-1002</strong>…, empezando en el número de <strong>B5</strong>.`,
      solution: [
        `Selecciona ${F(ANCHORS[3])}.`,
        `${F("&")} une texto con cada número de la secuencia.`,
        `Escribe ${F('="FAC-"&SECUENCIA(10,1,B5)')}.`,
      ],
      expectedResult: "FAC-1001 … FAC-1010",
      finalFormula: '="FAC-"&SECUENCIA(10,1,B5)',
    },
    {
      id: 5,
      level: "reto",
      title: "Tarea 5: Fechas de pago mensuales",
      description: conditions(
        `En <strong>${ANCHORS[4]}</strong>: genera la fecha de cada pago de un crédito:`,
        [
          "Tantas fechas como el <strong>Número de pagos</strong> de <strong>B6</strong>",
          "La primera es el <strong>Primer pago</strong> de <strong>B8</strong> (día 1 del mes)",
          "Cada fecha es el <strong>día 1 del mes siguiente</strong>",
        ]
      ),
      solution: [
        `Selecciona ${F(ANCHORS[4])}.`,
        "Los meses no tienen el mismo número de días, así que no sirve sumar 30. Usa FECHA y suma la secuencia al mes.",
        `Escribe ${F("=FECHA(AÑO(B8),MES(B8)+SECUENCIA(B6)-1,1)")}. FECHA acomoda sola los meses que pasan de 12.`,
      ],
      expectedResult: "01/02/2026, 01/03/2026 … 01/01/2027",
      finalFormula: "=FECHA(AÑO(B8),MES(B8)+SECUENCIA(B6)-1,1)",
    },
    {
      id: 6,
      level: "reto",
      title: "Tarea 6: Saldo después de cada pago",
      description: conditions(
        `En <strong>${ANCHORS[5]}</strong>: calcula el saldo que queda después de cada pago:`,
        [
          "Cada pago es <strong>Monto del crédito ÷ Número de pagos</strong> (B7 ÷ B6)",
          "Después del pago <em>n</em>, el saldo es <strong>B7 − n × pago</strong>",
        ],
        "Una sola fórmula para todos los pagos."
      ),
      solution: [
        `Selecciona ${F(ANCHORS[5])}.`,
        `${F("SECUENCIA(B6)")} da los números de pago 1, 2, 3… y se usan directamente en el cálculo.`,
        `Escribe ${F("=B7-SECUENCIA(B6)*B7/B6")}.`,
      ],
      expectedResult: "$110,000, $100,000 … $0",
      finalFormula: "=B7-SECUENCIA(B6)*B7/B6",
    },
    {
      id: 7,
      level: "reto",
      title: "Tarea 7: Calendario del mes",
      description: conditions(
        `En <strong>${ANCHORS[6]}</strong>: genera un calendario de <strong>6 semanas × 7 días</strong> para el mes de <strong>B9</strong>:`,
        [
          "Cada fila es una semana y empieza en <strong>lunes</strong>",
          "La primera celda es el lunes de la semana en que cae el día 1 del mes",
          "Cada celda es el día siguiente a la anterior",
        ]
      ),
      solution: [
        `Selecciona ${F(ANCHORS[6])}.`,
        `${F("DIASEM(B9,2)")} da 1 si B9 es lunes, … 7 si es domingo. Entonces ${F("B9-DIASEM(B9,2)+1")} es el lunes de esa semana.`,
        `Escribe ${F("=SECUENCIA(6,7,B9-DIASEM(B9,2)+1)")}. El área tiene formato \"d\" para mostrar solo el número del día.`,
      ],
      expectedResult: "Un calendario de lunes a domingo",
      finalFormula: "=SECUENCIA(6,7,B9-DIASEM(B9,2)+1)",
    },
  ],
};
